// Session 53 · Owner Onboarding Wizard (6 steps, autosave).
//
// Flow: Business → Address → Workspaces → Booking → Stripe → Review.
// Every step's Save button POSTs to /api/owner/onboarding/<step> which returns
// the full state (venue, workspaces, checklist). We rehydrate on mount from
// /api/owner/onboarding/state so owners can leave & return without losing
// progress.
//
// After publish, redirects to /owner/dashboard.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import NominatimAutocomplete from "../components/NominatimAutocomplete";
import PhotoDropzone from "../components/PhotoDropzone";
import { useAuth } from "../lib/auth";

const STEPS = [
  { id: 1, title: "Business", subtitle: "Name, type, description" },
  { id: 2, title: "Address", subtitle: "Location + geocode" },
  { id: 3, title: "Workspaces", subtitle: "Add rooms & spaces" },
  { id: 4, title: "Booking", subtitle: "Mode, pricing, policy" },
  { id: 5, title: "Payouts", subtitle: "Stripe Connect" },
  { id: 6, title: "Review", subtitle: "Publish or draft" },
];

const WORKSPACE_TYPES = [
  "Coworking Space", "Private Office", "Meeting Room",
  "Event Venue", "Day Office", "Custom",
];
const VENUE_TYPES = [
  "Coworking Space", "Event Venue", "Meeting Room", "Private Office",
  "Hotel", "Cafe", "Studio", "Other",
];

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const Card = ({ children, testid }) => (
  <div data-testid={testid} style={{
    background: "var(--navy-mid, #1a2340)",
    border: "1px solid rgba(212,168,75,0.16)",
    borderRadius: 14, padding: 28, marginBottom: 20,
  }}>{children}</div>
);

const Label = ({ children }) => (
  <label style={{
    fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.65)", display: "block", marginBottom: 6,
  }}>{children}</label>
);

const Input = ({ testid, ...rest }) => (
  <input {...rest} data-testid={testid} style={{
    width: "100%", background: "var(--navy-dark, #0f1729)",
    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
    color: "#fff", padding: "10px 12px", fontSize: 14,
    ...(rest.style || {}),
  }}/>
);

const Textarea = ({ testid, ...rest }) => (
  <textarea {...rest} data-testid={testid} style={{
    width: "100%", background: "var(--navy-dark, #0f1729)",
    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
    color: "#fff", padding: "10px 12px", fontSize: 14, minHeight: 80,
    resize: "vertical",
  }}/>
);

const Button = ({ variant = "primary", testid, ...rest }) => {
  const styles = {
    primary: { bg: "#B8913A", fg: "#fff", border: "#B8913A" },
    ghost: { bg: "transparent", fg: "rgba(255,255,255,0.8)", border: "rgba(255,255,255,0.16)" },
    outline: { bg: "transparent", fg: "#B8913A", border: "rgba(212,168,75,0.32)" },
  }[variant];
  return (
    <button type="button" {...rest} data-testid={testid} style={{
      padding: "10px 20px", borderRadius: 10, cursor: rest.disabled ? "not-allowed" : "pointer",
      border: `1px solid ${styles.border}`, background: styles.bg, color: styles.fg,
      fontSize: 13, fontWeight: 600, letterSpacing: "0.02em",
      opacity: rest.disabled ? 0.5 : 1,
      ...(rest.style || {}),
    }}/>
  );
};

const Row = ({ label, value, testid }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.05)" }} data-testid={testid}>
    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>{label}</span>
    <span style={{ color: "#fff", fontSize: 13, fontWeight: 600, textAlign: "right", maxWidth: "60%" }}>
      {value || <span style={{ color: "#94a3b8" }}>—</span>}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Progress ribbon
// ---------------------------------------------------------------------------

function StepRibbon({ currentStep, onJump }) {
  return (
    <div data-testid="onboarding-ribbon" style={{
      display: "grid", gridTemplateColumns: `repeat(${STEPS.length}, 1fr)`,
      gap: 8, marginBottom: 32,
    }}>
      {STEPS.map(s => {
        const active = s.id === currentStep;
        const done = s.id < currentStep;
        return (
          <button key={s.id} type="button" onClick={() => onJump(s.id)}
            data-testid={`onboarding-step-${s.id}`}
            style={{
              background: active ? "rgba(184,145,58,0.15)" : done ? "rgba(34,197,94,0.08)" : "rgba(15,23,41,0.04)",
              border: `1px solid ${active ? "#B8913A" : done ? "rgba(34,197,94,0.4)" : "rgba(15,23,41,0.12)"}`,
              borderRadius: 10, padding: "12px 10px",
              cursor: "pointer", textAlign: "left",
              transition: "all 0.15s ease",
            }}>
            <div style={{
              fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
              color: active ? "#B8913A" : done ? "#16a34a" : "rgba(15,23,41,0.55)",
              marginBottom: 4, fontWeight: 700,
            }}>
              {done ? "✓ " : ""}Step {s.id}
            </div>
            <div style={{ fontSize: 13, color: "var(--navy-dark, #0f1729)", fontWeight: 600 }}>{s.title}</div>
            <div style={{ fontSize: 11, color: "rgba(15,23,41,0.5)", marginTop: 2 }}>{s.subtitle}</div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Business
// ---------------------------------------------------------------------------

function Step1Business({ venue, onSave }) {
  const [form, setForm] = useState({
    organization: venue?.organization || "",
    name: venue?.name || "",
    venue_type: venue?.venue_type || venue?.type || "",
    description: venue?.description || "",
    logo_url: venue?.logo_url || "",
    cover_url: venue?.cover_url || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Venue name is required"); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Card testid="step-1-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Business information</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Introduce your venue. This is what members see first on the Métier directory.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <Label>Organization</Label>
          <Input testid="onboarding-org" value={form.organization}
                    onChange={e => set("organization", e.target.value)}
                    placeholder="Métier Coworking LLC"/>
        </div>
        <div>
          <Label>Venue name *</Label>
          <Input testid="onboarding-name" value={form.name}
                    onChange={e => set("name", e.target.value)}
                    placeholder="Manchester Loft"/>
        </div>
        <div>
          <Label>Venue type</Label>
          <select value={form.venue_type} onChange={e => set("venue_type", e.target.value)}
                  data-testid="onboarding-venue-type"
                  style={{
                    width: "100%", background: "var(--navy-dark, #0f1729)",
                    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
                    color: "#fff", padding: "10px 12px", fontSize: 14,
                  }}>
            <option value="">Select a type…</option>
            {VENUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label>Cover image</Label>
          <PhotoDropzone
            testid="onboarding-cover-drop"
            mode="single"
            aspect="16/9"
            value={form.cover_url}
            onChange={(url) => set("cover_url", url || "")}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Label>Description</Label>
          <Textarea testid="onboarding-description" value={form.description}
                      onChange={e => set("description", e.target.value)}
                      placeholder="Share what makes this space special…"/>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Label>Logo (optional)</Label>
          <PhotoDropzone
            testid="onboarding-logo-drop"
            mode="single"
            aspect="1/1"
            value={form.logo_url}
            onChange={(url) => set("logo_url", url || "")}
          />
        </div>
      </div>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button testid="onboarding-step1-save" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : "Save & continue →"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Address
// ---------------------------------------------------------------------------

function Step2Address({ venue, onSave }) {
  const [form, setForm] = useState({
    address_line_1: venue?.address_line_1 || "",
    address_line_2: venue?.address_line_2 || "",
    city: venue?.city || "",
    county: venue?.county || "",
    state: venue?.state || "",
    postal_code: venue?.postal_code || "",
    country: venue?.country || "US",
    latitude: venue?.latitude ?? null,
    longitude: venue?.longitude ?? null,
    timezone: venue?.timezone || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.address_line_1 || !form.city || !form.state || !form.postal_code) {
      toast.error("Address, city, state and ZIP are required"); return;
    }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Card testid="step-2-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Address</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Type a street address to autocomplete via OpenStreetMap. We&apos;ll geocode
        it so your venue shows on the map + in radius searches.
      </p>
      <Label>Street address *</Label>
      <NominatimAutocomplete
        value={form.address_line_1}
        onChange={v => setForm(f => ({ ...f, address_line_1: v }))}
        testid="onboarding-addr-auto"
        onPick={(picked) => {
          setForm(f => ({
            ...f,
            address_line_1: picked.address_line_1 || f.address_line_1,
            city: picked.city || f.city,
            county: picked.county ?? f.county,
            state: picked.state || f.state,
            postal_code: picked.postal_code || f.postal_code,
            country: picked.country || f.country,
            latitude: picked.latitude ?? f.latitude,
            longitude: picked.longitude ?? f.longitude,
          }));
          toast.success("Address filled from OpenStreetMap");
        }}
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginTop: 16 }}>
        <div><Label>Suite / Apt</Label><Input testid="onboarding-addr2"
          value={form.address_line_2} onChange={e => setForm(f => ({ ...f, address_line_2: e.target.value }))}/></div>
        <div><Label>City *</Label><Input testid="onboarding-city"
          value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}/></div>
        <div><Label>County</Label><Input testid="onboarding-county"
          value={form.county} onChange={e => setForm(f => ({ ...f, county: e.target.value }))}/></div>
        <div><Label>State *</Label><Input testid="onboarding-state"
          value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}/></div>
        <div><Label>ZIP *</Label><Input testid="onboarding-zip"
          value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}/></div>
        <div><Label>Country</Label><Input testid="onboarding-country"
          value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}/></div>
      </div>
      {form.latitude && form.longitude && (
        <div style={{ marginTop: 16, padding: 12, background: "rgba(34,197,94,0.08)",
                        border: "1px solid rgba(34,197,94,0.22)", borderRadius: 8 }}
              data-testid="onboarding-latlng">
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
                          color: "#22c55e", marginBottom: 4 }}>
            📍 Geocoded
          </div>
          <div style={{ color: "#fff", fontSize: 13 }}>
            {Number(form.latitude).toFixed(4)}, {Number(form.longitude).toFixed(4)}
          </div>
          <a href={`https://www.openstreetmap.org/?mlat=${form.latitude}&mlon=${form.longitude}&zoom=15`}
              target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: "#B8913A", textDecoration: "none", marginTop: 4, display: "inline-block" }}>
            Preview on OpenStreetMap →
          </a>
        </div>
      )}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button testid="onboarding-step2-save" onClick={submit} disabled={saving}>
          {saving ? "Geocoding…" : "Save & continue →"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Workspaces
// ---------------------------------------------------------------------------

function Step3Workspaces({ workspaces, onAdd, onRemove, onContinue }) {
  const [form, setForm] = useState({
    name: "", workspace_type: "Coworking Space", capacity: "",
    description: "", amenities: "", photos: [],
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Workspace name is required"); return; }
    setSaving(true);
    try {
      await onAdd({
        name: form.name,
        workspace_type: form.workspace_type,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        description: form.description,
        amenities: form.amenities ? form.amenities.split(",").map(s => s.trim()).filter(Boolean) : [],
        photos: form.photos,
      });
      setForm({ name: "", workspace_type: "Coworking Space", capacity: "",
                    description: "", amenities: "", photos: [] });
    } finally { setSaving(false); }
  };

  return (
    <Card testid="step-3-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Workspaces</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Add every bookable space in your venue. You can add more anytime after publishing.
      </p>
      {workspaces.length > 0 && (
        <div style={{ marginBottom: 20 }} data-testid="onboarding-ws-list">
          {workspaces.map(w => (
            <div key={w.workspace_id} style={{
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 10, padding: "12px 16px", marginBottom: 8,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }} data-testid={`onboarding-ws-${w.workspace_id}`}>
              <div>
                <div style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>{w.name}</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                  {w.workspace_type}{w.capacity ? ` · Capacity ${w.capacity}` : ""}
                  {Array.isArray(w.photos) && w.photos.length > 0 && ` · 📸 ${w.photos.length}`}
                </div>
              </div>
              <button type="button" onClick={() => onRemove(w.workspace_id)}
                data-testid={`onboarding-ws-remove-${w.workspace_id}`}
                style={{ background: "none", border: "none", color: "#ef4444",
                          fontSize: 12, cursor: "pointer" }}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ background: "rgba(212,168,75,0.05)", border: "1px dashed rgba(212,168,75,0.28)",
                      borderRadius: 10, padding: 20 }}>
        <div style={{ color: "#B8913A", fontSize: 11, letterSpacing: "0.16em",
                        textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>
          Add a workspace
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div><Label>Name *</Label><Input testid="onboarding-ws-name"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Main Floor"/></div>
          <div><Label>Type</Label>
            <select value={form.workspace_type} onChange={e => setForm({ ...form, workspace_type: e.target.value })}
              data-testid="onboarding-ws-type"
              style={{ width: "100%", background: "var(--navy-dark, #0f1729)",
                          border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
                          color: "#fff", padding: "10px 12px", fontSize: 14 }}>
              {WORKSPACE_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div><Label>Capacity</Label><Input testid="onboarding-ws-capacity" type="number"
            value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} placeholder="12"/></div>
        </div>
        <Label>Description</Label>
        <Textarea testid="onboarding-ws-description" value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="What makes this space unique?"/>
        <div style={{ marginTop: 12 }}>
          <Label>Amenities (comma-separated)</Label>
          <Input testid="onboarding-ws-amenities" value={form.amenities}
            onChange={e => setForm({ ...form, amenities: e.target.value })}
            placeholder="Wi-Fi, Whiteboard, Coffee"/>
        </div>
        <div style={{ marginTop: 14 }}>
          <Label>Workspace photos</Label>
          <PhotoDropzone
            testid="onboarding-ws-photos"
            mode="multiple"
            maxCount={8}
            aspect="4/3"
            value={form.photos}
            onChange={(urls) => setForm({ ...form, photos: urls })}
          />
        </div>
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outline" testid="onboarding-ws-add" onClick={submit} disabled={saving}>
            {saving ? "Adding…" : "+ Add workspace"}
          </Button>
        </div>
      </div>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button testid="onboarding-step3-continue" onClick={onContinue} disabled={workspaces.length === 0}>
          Continue →
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Booking
// ---------------------------------------------------------------------------

function Step4Booking({ workspaces, onSave, onContinue }) {
  const [selectedId, setSelectedId] = useState(workspaces[0]?.workspace_id || "");
  const selected = workspaces.find(w => w.workspace_id === selectedId);
  const [form, setForm] = useState(() => ({
    booking_mode: selected?.owner_booking_settings?.booking_mode || "instant",
    external_url: selected?.external_booking_url || "",
    hourly: selected?.rates?.hourly || "",
    half_day: selected?.rates?.half_day || "",
    daily: selected?.rates?.daily || "",
    deposit_percent: selected?.owner_booking_settings?.deposit_percent || 0,
    cancellation_policy: selected?.owner_booking_settings?.cancellation_policy || "moderate",
    buffer_before_min: selected?.owner_booking_settings?.buffer_before_min || 15,
    buffer_after_min: selected?.owner_booking_settings?.buffer_after_min || 15,
    request_expiry_hours: selected?.owner_booking_settings?.request_expiry_hours || 48,
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selected) return;
    setForm({
      booking_mode: selected.owner_booking_settings?.booking_mode || "instant",
      external_url: selected.external_booking_url || "",
      hourly: selected.rates?.hourly || "",
      half_day: selected.rates?.half_day || "",
      daily: selected.rates?.daily || "",
      deposit_percent: selected.owner_booking_settings?.deposit_percent || 0,
      cancellation_policy: selected.owner_booking_settings?.cancellation_policy || "moderate",
      buffer_before_min: selected.owner_booking_settings?.buffer_before_min || 15,
      buffer_after_min: selected.owner_booking_settings?.buffer_after_min || 15,
      request_expiry_hours: selected.owner_booking_settings?.request_expiry_hours || 48,
    });
  }, [selectedId, selected]);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const rates = {};
      if (form.hourly) rates.hourly = parseFloat(form.hourly);
      if (form.half_day) rates.half_day = parseFloat(form.half_day);
      if (form.daily) rates.daily = parseFloat(form.daily);
      await onSave({
        workspace_id: selected.workspace_id,
        booking_mode: form.booking_mode,
        external_url: form.booking_mode === "external" ? form.external_url : null,
        rates,
        deposit_percent: parseFloat(form.deposit_percent) || 0,
        cancellation_policy: form.cancellation_policy,
        buffer_before_min: parseInt(form.buffer_before_min, 10) || 0,
        buffer_after_min: parseInt(form.buffer_after_min, 10) || 0,
        request_expiry_hours: parseInt(form.request_expiry_hours, 10) || 48,
      });
    } finally { setSaving(false); }
  };

  return (
    <Card testid="step-4-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Booking configuration</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Configure booking mode, pricing and policy for each workspace.
      </p>
      <Label>Configure for</Label>
      <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
        data-testid="onboarding-booking-select"
        style={{ width: "100%", background: "var(--navy-dark, #0f1729)",
                    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
                    color: "#fff", padding: "10px 12px", fontSize: 14, marginBottom: 20 }}>
        {workspaces.map(w => {
          const configured = w.owner_booking_settings?.configured;
          return (
            <option key={w.workspace_id} value={w.workspace_id}>
              {configured ? "✓ " : ""}{w.name}
            </option>
          );
        })}
      </select>

      <Label>Booking mode</Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { key: "instant", label: "Instant Book", desc: "Charge on booking" },
          { key: "request", label: "Request to Book", desc: "Approve each request" },
          { key: "external", label: "External Booking", desc: "Deep-link elsewhere" },
        ].map(o => (
          <button key={o.key} type="button" onClick={() => setForm(f => ({ ...f, booking_mode: o.key }))}
            data-testid={`onboarding-mode-${o.key}`}
            style={{
              padding: "14px 16px", borderRadius: 10, cursor: "pointer",
              background: form.booking_mode === o.key ? "rgba(184,145,58,0.15)" : "transparent",
              border: `1px solid ${form.booking_mode === o.key ? "#B8913A" : "rgba(255,255,255,0.1)"}`,
              textAlign: "left",
            }}>
            <div style={{ color: "#fff", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{o.label}</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{o.desc}</div>
          </button>
        ))}
      </div>

      {form.booking_mode === "external" && (
        <div style={{ marginBottom: 16 }}>
          <Label>External booking URL</Label>
          <Input testid="onboarding-external-url" value={form.external_url}
            onChange={e => setForm({ ...form, external_url: e.target.value })}
            placeholder="https://…"/>
        </div>
      )}

      {form.booking_mode !== "external" && (
        <>
          <Label>Rates (USD)</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
            <div><Label>Hourly</Label><Input testid="onboarding-rate-hourly" type="number" value={form.hourly}
              onChange={e => setForm({ ...form, hourly: e.target.value })} placeholder="0"/></div>
            <div><Label>Half day</Label><Input testid="onboarding-rate-halfday" type="number" value={form.half_day}
              onChange={e => setForm({ ...form, half_day: e.target.value })} placeholder="0"/></div>
            <div><Label>Full day</Label><Input testid="onboarding-rate-daily" type="number" value={form.daily}
              onChange={e => setForm({ ...form, daily: e.target.value })} placeholder="0"/></div>
          </div>
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <div><Label>Deposit %</Label><Input testid="onboarding-deposit" type="number" min={0} max={100}
          value={form.deposit_percent} onChange={e => setForm({ ...form, deposit_percent: e.target.value })}/></div>
        <div><Label>Cancellation policy</Label>
          <select value={form.cancellation_policy} onChange={e => setForm({ ...form, cancellation_policy: e.target.value })}
            data-testid="onboarding-cancel-policy"
            style={{ width: "100%", background: "var(--navy-dark, #0f1729)",
                          border: "1px solid rgba(212,168,75,0.22)", borderRadius: 8,
                          color: "#fff", padding: "10px 12px", fontSize: 14 }}>
            <option value="flexible">Flexible (24h)</option>
            <option value="moderate">Moderate (72h)</option>
            <option value="strict">Strict (7d)</option>
          </select>
        </div>
        <div><Label>Buffer before (min)</Label><Input testid="onboarding-buffer-before" type="number"
          value={form.buffer_before_min} onChange={e => setForm({ ...form, buffer_before_min: e.target.value })}/></div>
        <div><Label>Buffer after (min)</Label><Input testid="onboarding-buffer-after" type="number"
          value={form.buffer_after_min} onChange={e => setForm({ ...form, buffer_after_min: e.target.value })}/></div>
        {form.booking_mode === "request" && (
          <div><Label>Request expiry (h)</Label><Input testid="onboarding-request-expiry" type="number" min={1} max={168}
            value={form.request_expiry_hours} onChange={e => setForm({ ...form, request_expiry_hours: e.target.value })}/></div>
        )}
      </div>

      <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button variant="outline" testid="onboarding-step4-save" onClick={submit} disabled={saving || !selected}>
          {saving ? "Saving…" : "Save this workspace"}
        </Button>
        <Button testid="onboarding-step4-continue" onClick={onContinue}
          disabled={!workspaces.some(w => w.owner_booking_settings?.configured)}>
          Continue →
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Stripe
// ---------------------------------------------------------------------------

function Step5Stripe({ venue, onSave, onContinue, onRefresh }) {
  const [saving, setSaving] = useState(null);
  const [checking, setChecking] = useState(false);
  const returnHandledRef = useRef(false);

  const connected = !!venue?.stripe_connected_at;
  const skipped = !!venue?.stripe_skipped_at;
  const started = !!venue?.stripe_onboarding_started_at && !connected;
  const acctId = venue?.stripe_account_id;

  // Session 53.6 · Poll Stripe status when the popup returns or when the
  // component mounts after a "?stripe=return" redirect. Also poll on focus
  // so re-focusing the tab after finishing in Stripe triggers a check.
  const checkStatus = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    try {
      const r = await api.get("/owner/onboarding/stripe/status");
      if (r.data?.connected) {
        toast.success("Stripe onboarding complete — payouts enabled!");
        await onRefresh();
      } else if (started && r.data?.details_submitted) {
        toast.info("Stripe review in progress. We'll flip payouts on once approved.");
      }
    } catch (e) {
      // silent — user may not have started yet
    } finally {
      setChecking(false);
    }
  }, [checking, started, onRefresh]);

  useEffect(() => {
    // If the wizard mounts with ?stripe=return, check status ONCE and strip
    // the query string. Guarded by a ref so subsequent dep re-runs of this
    // effect don't re-fire the check.
    if (!returnHandledRef.current) {
      const params = new URLSearchParams(window.location.search);
      if (params.get("stripe") === "return") {
        returnHandledRef.current = true;
        checkStatus();
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
    // Poll on tab focus. Deps include `started`, `connected`, `checkStatus`
    // so the handler always reads the freshest state — fixes the empty-deps
    // stale-closure bug reported in iteration_27 where focus polling never
    // fired if the venue loaded asynchronously after first render.
    const onFocus = () => { if (started && !connected) checkStatus(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [started, connected, checkStatus]);

  const startStripe = async () => {
    setSaving("stripe");
    try {
      const origin = window.location.origin;
      const r = await api.post("/owner/onboarding/stripe/link", {
        return_url: `${origin}/owner/onboard?stripe=return`,
        refresh_url: `${origin}/owner/onboard?stripe=refresh`,
      });
      const url = r.data?.onboarding_url;
      if (!url) { toast.error("Could not create Stripe onboarding link"); return; }
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked — same-tab redirect. Skip the "new tab" toast to
        // avoid confusing copy.
        window.location.href = url;
        return;
      }
      toast.info("Complete Stripe onboarding in the new tab. We'll refresh when you return.");
      await onRefresh();  // pull stripe_onboarding_started_at
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe error");
    } finally {
      setSaving(null);
    }
  };

  const handleLegacy = async (action) => {
    setSaving(action);
    try { await onSave({ action }); } finally { setSaving(null); }
  };

  return (
    <Card testid="step-5-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Stripe Connect</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Connect Stripe to receive payouts. You can publish without Stripe, but{" "}
        <strong style={{ color: "#B8913A" }}>you will not receive any funds until Stripe onboarding is complete.</strong>
      </p>
      {connected ? (
        <div style={{ padding: 16, background: "rgba(34,197,94,0.08)",
                        border: "1px solid rgba(34,197,94,0.22)", borderRadius: 8 }}
              data-testid="onboarding-stripe-connected">
          <div style={{ color: "#22c55e", fontSize: 13, fontWeight: 600 }}>✓ Stripe connected</div>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>
            Payouts are enabled. You can publish and start receiving reservations.
          </div>
          {acctId && (
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 6 }}>
              Account: <code>{acctId}</code>
            </div>
          )}
        </div>
      ) : started ? (
        <div style={{ padding: 16, background: "rgba(184,145,58,0.08)",
                        border: "1px solid rgba(212,168,75,0.28)", borderRadius: 8 }}
              data-testid="onboarding-stripe-inprogress">
          <div style={{ color: "#B8913A", fontSize: 13, fontWeight: 600 }}>
            ⏳ Stripe onboarding started
          </div>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 4 }}>
            Finish in the Stripe tab, then we&apos;ll flip payouts on automatically.
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
            <Button variant="outline" testid="onboarding-stripe-resume"
              onClick={startStripe} disabled={!!saving}>
              {saving === "stripe" ? "…" : "Resume Stripe"}
            </Button>
            <Button variant="ghost" testid="onboarding-stripe-check"
              onClick={checkStatus} disabled={checking}>
              {checking ? "Checking…" : "I'm done — check status"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <button type="button" onClick={startStripe} disabled={!!saving}
              data-testid="onboarding-stripe-connect"
              style={{
                padding: 24, borderRadius: 12, cursor: "pointer",
                background: "#635BFF", border: "1px solid #635BFF",
                color: "#fff", textAlign: "left",
              }}>
              <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.7, marginBottom: 6 }}>
                Recommended
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {saving === "stripe" ? "Opening Stripe…" : "Connect Stripe"}
              </div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>Set up payouts + tax reporting in ~3 minutes</div>
            </button>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button type="button" onClick={() => handleLegacy("continue_later")} disabled={!!saving}
                data-testid="onboarding-stripe-later"
                style={{
                  padding: "14px 20px", borderRadius: 10, cursor: "pointer",
                  background: "transparent", border: "1px solid rgba(255,255,255,0.16)",
                  color: "rgba(255,255,255,0.85)", textAlign: "left", fontSize: 13, fontWeight: 600,
                }}>
                Continue later
              </button>
              <button type="button" onClick={() => handleLegacy("skip")} disabled={!!saving}
                data-testid="onboarding-stripe-skip"
                style={{
                  padding: "14px 20px", borderRadius: 10, cursor: "pointer",
                  background: "transparent", border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.55)", textAlign: "left", fontSize: 13, fontWeight: 600,
                }}>
                Skip for now
              </button>
              <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4, lineHeight: 1.5 }}>
                ⚠ Payouts will be held until Stripe onboarding is finished.
              </div>
            </div>
          </div>
          {skipped && (
            <div style={{ marginTop: 12, fontSize: 12, color: "rgba(255,255,255,0.55)" }}
                  data-testid="onboarding-stripe-skipped-hint">
              Skipped — you can re-open Stripe from the Owner Dashboard at any time.
            </div>
          )}
        </>
      )}
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <Button testid="onboarding-step5-continue" onClick={onContinue}>Continue →</Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — Review & Publish
// ---------------------------------------------------------------------------

function Step6Review({ venue, workspaces, checklist, onPublish, onSaveDraft }) {
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    setPublishing(true);
    try { await onPublish(); } finally { setPublishing(false); }
  };

  return (
    <Card testid="step-6-card">
      <h2 style={{ color: "#fff", marginTop: 0, marginBottom: 8, fontSize: 22 }}>Review &amp; publish</h2>
      <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 0, marginBottom: 20 }}>
        Everything looks good? Publish to make this venue live on the Métier directory.
      </p>

      <div data-testid="onboarding-summary">
        <h3 style={{ color: "#fff", marginBottom: 8, fontSize: 15 }}>Venue</h3>
        <Row label="Name" value={venue?.name} testid="summary-name"/>
        <Row label="Organization" value={venue?.organization}/>
        <Row label="Type" value={venue?.venue_type}/>
        <Row label="Address" value={venue?.address_line_1 ? `${venue.address_line_1}, ${venue.city}, ${venue.state} ${venue.postal_code}` : null} testid="summary-address"/>
        <Row label="Coordinates" value={venue?.latitude ? `${venue.latitude.toFixed(4)}, ${venue.longitude.toFixed(4)}` : null}/>

        <h3 style={{ color: "#fff", marginTop: 20, marginBottom: 8, fontSize: 15 }}>Workspaces ({workspaces.length})</h3>
        {workspaces.map(w => {
          const s = w.owner_booking_settings || {};
          const r = w.rates || {};
          return (
            <div key={w.workspace_id} style={{
              padding: 12, background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, marginBottom: 8,
            }} data-testid={`summary-ws-${w.workspace_id}`}>
              <div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
                {w.name} <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}>· {w.workspace_type}</span>
              </div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 4 }}>
                Booking: {s.booking_mode || "—"} · Cancel: {s.cancellation_policy || "—"}
                {" · "}
                {r.hourly ? `$${r.hourly}/hr ` : ""}{r.half_day ? `$${r.half_day}/half ` : ""}{r.daily ? `$${r.daily}/day` : ""}
              </div>
            </div>
          );
        })}

        <h3 style={{ color: "#fff", marginTop: 20, marginBottom: 8, fontSize: 15 }}>Stripe status</h3>
        <Row label="Payouts" value={venue?.stripe_connected_at ? "Connected" :
          venue?.stripe_deferred_at ? "Deferred" : venue?.stripe_skipped_at ? "Skipped" : "Not set up"}/>
      </div>

      <ChecklistCard checklist={checklist}/>

      <div className="legal-inline-ack" data-testid="onboarding-marketplace-ack" style={{marginTop:16, background:"rgba(184,145,58,0.1)", borderLeftColor:"#B8913A", color:"rgba(255,255,255,0.88)"}}>
        By listing your venue on Métier, you acknowledge Métier's{" "}
        <a href="/legal/terms" target="_blank" rel="noopener" style={{color:"#F4D28A"}}>Terms of Service</a>,{" "}
        <a href="/legal/privacy" target="_blank" rel="noopener" style={{color:"#F4D28A"}}>Privacy Policy</a>,
        booking policies, and Stripe Connect payout requirements. You confirm you have authority to list this venue on behalf of your organization and to bind it to these terms.
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "flex-end" }}>
        <Button variant="ghost" testid="onboarding-save-draft" onClick={onSaveDraft}>Save as draft</Button>
        <Button testid="onboarding-publish" onClick={publish}
          disabled={!checklist.ready_to_publish || publishing}>
          {publishing ? "Publishing…" : "🎉 Publish venue"}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Completion checklist card (sidebar-style, shown in Step 6)
// ---------------------------------------------------------------------------

function ChecklistCard({ checklist }) {
  const items = [
    { key: "venue_created", label: "Venue created" },
    { key: "address_verified", label: "Address verified" },
    { key: "workspace_added", label: "Workspace added" },
    { key: "pricing_configured", label: "Pricing configured" },
    { key: "booking_enabled", label: "Booking enabled" },
    { key: "stripe_connected", label: "Stripe connected" },
    { key: "ready_to_publish", label: "Ready to publish" },
  ];
  return (
    <div style={{ marginTop: 24, padding: 16, background: "rgba(212,168,75,0.05)",
                    border: "1px solid rgba(212,168,75,0.22)", borderRadius: 12 }}
          data-testid="onboarding-checklist">
      <div style={{ color: "#B8913A", fontSize: 11, letterSpacing: "0.16em",
                      textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>
        Completion checklist
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
        {items.map(i => (
          <div key={i.key} data-testid={`checklist-${i.key}`}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13,
                          color: checklist[i.key] ? "#22c55e" : "rgba(255,255,255,0.5)" }}>
            <span style={{ fontSize: 15 }}>{checklist[i.key] ? "✔" : "○"}</span>
            {i.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main container
// ---------------------------------------------------------------------------

export default function OwnerOnboarding() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/owner/onboarding/state");
      setState(r.data);
      setStep(r.data.current_step || 1);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login?redirect=/owner/onboard");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!authLoading && !user) { navigate("/login?redirect=/owner/onboard"); return; }
    if (user) refresh();
  }, [user, authLoading, refresh, navigate]);

  const call = async (path, body = {}, method = "post") => {
    const r = await api[method](path, body);
    setState(r.data);
    setStep(r.data.current_step || step);
    return r.data;
  };

  const saveBusiness = async (form) => {
    await call("/owner/onboarding/business", form);
    toast.success("Saved");
    setStep(2);
  };
  const saveAddress = async (form) => {
    await call("/owner/onboarding/address", form);
    toast.success("Address saved & geocoded");
    setStep(3);
  };
  const addWorkspace = async (form) => {
    await call("/owner/onboarding/workspaces", form);
    toast.success("Workspace added");
  };
  const removeWorkspace = async (wid) => {
    await api.delete(`/owner/onboarding/workspaces/${wid}`);
    await refresh();
    toast.success("Workspace removed");
  };
  const saveBooking = async (form) => {
    await call("/owner/onboarding/booking", form);
    toast.success("Booking configured");
  };
  const saveStripe = async (form) => {
    await call("/owner/onboarding/stripe", form);
    toast.success(form.action === "connected" ? "Stripe connected" : "Choice saved");
    if (form.action !== "skip" && form.action !== "continue_later") setStep(6);
  };
  const publish = async () => {
    try {
      const r = await api.post("/owner/onboarding/publish");
      toast.success(`🎉 Published — ${r.data.workspaces_published} workspaces live`);
      setTimeout(() => navigate(r.data.redirect_to || "/owner/dashboard"), 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };
  const saveDraft = () => {
    toast.success("Draft saved — you can pick up where you left off anytime");
    navigate("/owner/dashboard");
  };

  const venue = state?.venue;
  const workspaces = state?.workspaces || [];
  const checklist = state?.checklist || {};

  if (loading || authLoading) {
    return (
      <div className="m-container" style={{ padding: "80px 24px", textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
        Loading your onboarding…
      </div>
    );
  }

  return (
    <div className="m-container" style={{ padding: "40px 24px", maxWidth: 1080, margin: "0 auto" }}
        data-testid="owner-onboarding">
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
                        color: "var(--gold, #B8913A)", marginBottom: 6 }}>
          Onboarding
        </div>
        <h1 style={{ fontSize: 32, color: "var(--navy-dark, #0f1729)", margin: 0, fontWeight: 700 }}>
          Set up your venue on Métier
        </h1>
        <p style={{ color: "rgba(30,41,59,0.65)", fontSize: 14, marginTop: 8, maxWidth: 620 }}>
          Every step autosaves. Leave whenever you want and pick up right where you left off.
        </p>
      </div>
      <StepRibbon currentStep={step} onJump={setStep}/>
      {step === 1 && <Step1Business venue={venue} onSave={saveBusiness}/>}
      {step === 2 && <Step2Address venue={venue} onSave={saveAddress}/>}
      {step === 3 && <Step3Workspaces workspaces={workspaces}
        onAdd={addWorkspace} onRemove={removeWorkspace} onContinue={() => setStep(4)}/>}
      {step === 4 && <Step4Booking workspaces={workspaces}
        onSave={saveBooking} onContinue={() => setStep(5)}/>}
      {step === 5 && <Step5Stripe venue={venue} onSave={saveStripe} onContinue={() => setStep(6)} onRefresh={refresh}/>}
      {step === 6 && <Step6Review venue={venue} workspaces={workspaces} checklist={checklist}
        onPublish={publish} onSaveDraft={saveDraft}/>}
    </div>
  );
}
